import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import RootLoader from '../../components/root-loader/index';
import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { accessTokenStorage } from '../../storage/auth/index';
import { Company } from '../../types/company.types';
import { useAppQuery } from '../use-app-query.hook';
import { useLocale } from '../use-locale.hook';
import { selectedCompanyStorage } from '../../storage/company/selected-company.util';
import { useLayoutEffect } from 'react';

export const useSelected = ({
  redirectOnSelectedCompanyError,
}: { redirectOnSelectedCompanyError?: boolean } = {}) => {
  const accessToken = accessTokenStorage(({ value }) => value);

  const setCompanyId = selectedCompanyStorage(({ setCompanyId }) => setCompanyId);
  const setCompany = selectedCompanyStorage(({ setCompany }) => setCompany);
  const guestAccessRole = selectedCompanyStorage(({ company }) => company?.stakeholder?.accessRole);

  const navigate = useNavigate();
  const { locale } = useLocale();
  const queryClient = useQueryClient();

  const { data: selectedCompany, isLoading } = useAppQuery({
    queryKey: [QueryKey.GET_SELECTED_COMPANY],
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: guestAccessRole ? 30_000 : false,
    queryFn: async () =>
      (await ApiService.get<Company | null>(
        { endpoint: BackendRoute.COMPANIES, routePath: ['selected'] },
        { locale },
      )) ?? null,
    onError: () => {
      if (!redirectOnSelectedCompanyError) return;
      navigate('/error-page');
    },
    enabled: !!accessToken,
  });

  useLayoutEffect(() => {
    if (!selectedCompany?.id) return;
    setCompanyId(selectedCompany.id);
    setCompany(selectedCompany);
  }, [selectedCompany, setCompany, setCompanyId]);

  const invalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SELECTED_COMPANY] });
  };

  return {
    selectedCompanyId: selectedCompany?.id || '',
    // Query cache stores `null` (React Query forbids undefined). Callers still destructure
    // `selectedCompany: { currency } = {}`, and that default only applies to `undefined`.
    selectedCompany: selectedCompany ?? undefined,
    isLoading,
    invalidateQuery,
  };
};
